import { WidgetAdditionalChannel } from "@aichat/types/widget";
import React from "react";
import { ChannelListEnum } from "@aichat/types/index";
import MessengerIcon from "../../icons/MessengerIcon";
import InstagramIcon from "../../icons/InstagramIcon";
import WhatsappIcon from "../../icons/WhatsappIcon";
import { Typography } from "../typography";
import { ChevronRight } from "lucide-react";
import { capitalize } from "@aichat/aichat-lib";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../dropdown-menu";
import { cn } from "@aichat/ui/lib/utils";

type AdditionalChannelsProps = {
  channels: WidgetAdditionalChannel[];
  variant: "large" | "small";
  textColor: string;
};

function ChannelIcon({ type }: { type: ChannelListEnum }) {
  switch (type) {
    case ChannelListEnum.FACEBOOK:
      return <MessengerIcon filled className="size-6" />;
    case ChannelListEnum.INSTAGRAM:
      return <InstagramIcon filled className="size-6" />;
    case ChannelListEnum.WHATSAPP:
      return <WhatsappIcon filled className="size-6" />;
    default:
      return null;
  }
}

const StackedIcons = ({
  items,
  offset = -10,
}: {
  items: React.ReactNode[];
  offset?: number;
}) => {
  return (
    <div className="flex items-center">
      {items.map((item, index) => (
        <div
          key={index}
          className="relative"
          style={{
            marginLeft: index === 0 ? 0 : offset,
            zIndex: items.length - index,
          }}
        >
          {item}
        </div>
      ))}
    </div>
  );
};

function AdditionalChannels(props: AdditionalChannelsProps) {
  const onlyHasOneChannel = props.channels.length === 1;
  const firstChannel = props.channels.at(0);

  if (props.channels.length <= 0) return null;

  return (
    <>
      {onlyHasOneChannel && firstChannel ? (
        <Trigger
          textColor={props.textColor}
          onlyIcon={props.variant === "small"}
          icon={<ChannelIcon type={firstChannel.type} />}
          text={`Chat with us on ${capitalize(firstChannel.type.toLowerCase())}`}
          href={firstChannel.link}
        />
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <Trigger
              textColor={props.textColor}
              onlyIcon={props.variant === "small"}
              icon={
                <StackedIcons
                  items={props.channels.map((channel) => (
                    <ChannelIcon key={channel.type} type={channel.type} />
                  ))}
                />
              }
              text="Chat with us on other channels"
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent className="p-2 flex flex-col gap-2 w-[288px]">
            {props.channels.map((channel) => (
              <Trigger
                className="p-[6px] hover:bg-neutral-100 transition-colors rounded-lg"
                icon={<ChannelIcon type={channel.type} />}
                text={`Chat with us on ${capitalize(channel.type.toLowerCase())}`}
                href={channel.link}
                hideChevron
                textColor="black"
              />
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );
}

function Trigger({
  icon,
  text,
  href,
  className,
  hideChevron,
  onlyIcon,
  textColor,
}: {
  icon: React.ReactNode;
  text: string;
  href?: string;
  className?: string;
  hideChevron?: boolean;
  onlyIcon?: boolean;
  textColor: string;
}) {
  if (href)
    return (
      <a
        href={href}
        target="_blank"
        className={cn("flex items-center gap-2", className)}
      >
        {icon}
        {!onlyIcon && (
          <Typography variant="subtleMedium" style={{ color: textColor }}>
            {text}
          </Typography>
        )}

        {!onlyIcon && !hideChevron && (
          <ChevronRight className="size-4" style={{ color: textColor }} />
        )}
      </a>
    );

  return (
    <div
      role="button"
      tabIndex={0}
      className={cn("flex items-center gap-2", className)}
    >
      {icon}
      {!onlyIcon && (
        <Typography
          variant="subtleMedium"
          className="underline"
          style={{ color: textColor }}
        >
          {text}
        </Typography>
      )}
      {!onlyIcon && !hideChevron && (
        <ChevronRight className="size-4" style={{ color: textColor }} />
      )}
    </div>
  );
}

export default AdditionalChannels;
